<script setup>
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import { h, onMounted, reactive, ref, unref, watch } from "vue";
import { getRequest } from "@/utils/request.js";
import CompanyAuditFormModal from "@/views/company/audit/modules/CompanyAuditFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const menuCurrent = ref(["DOING"]);
const menuItems = ref([
  {
    key: "DOING",
    label: "待审核",
    title: "待审核",
  },
  {
    key: "all",
    label: "全部",
    title: "全部",
  },
  {
    key: "NOT_DONE",
    label: "未认证",
    title: "未认证",
  },
  {
    key: "REJECT",
    label: "审核驳回",
    title: "审核驳回",
  },
]);

const columns = [
  {
    key: "type",
    dataIndex: "type",
    title: "类型",
    width: "15%",
  },
  {
    key: "name",
    dataIndex: "name",
    title: "公司/个人名称",
    width: "15%",
  },
  {
    key: "authenticationStatus",
    dataIndex: "authenticationStatus",
    title: "状态",
    width: "15%",
  },
  {
    key: "code",
    dataIndex: "code",
    title: "统一社会信用代码/身份证号",
    width: "15%",
  },
  {
    key: "authenticationInfo",
    dataIndex: "authenticationInfo",
    title: "认证信息",
    width: "15%",
  },
  {
    key: "createTime",
    dataIndex: "createTime",
    title: "注册时间",
    width: "15%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

watch(menuCurrent, () => {
  loadData();
});

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign(
    {},
    unref(searchFormModel),
    {
      pageNo: current,
      pageSize,
    },
    {
      authenticationStatus:
        unref(menuCurrent)[0] === "all" ? "" : unref(menuCurrent)[0],
    },
  );
  getRequest("/company/temp", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAudit = (record) => {
  modalRef.value.title = "企业信息认证审核";
  modalRef.value.handleAudit(record);
  modalRef.value.isAudit = true;
};

const handlePreview = (record) => {
  modalRef.value.title = "企业信息认证查看";
  modalRef.value.handleAudit(record);
  modalRef.value.isAudit = false;
};
const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 8, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="公司/个人名称" name="name">
              <a-input
                v-model:value.trim="searchFormModel.name"
                placeholder="请输入公司/个人名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="统一社会信用代码/身份证号" name="code">
              <a-input
                v-model:value.trim="searchFormModel.code"
                placeholder="请输入统一社会信用代码/身份证号"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <a-menu
        style="padding-bottom: 10px"
        v-model:selectedKeys="menuCurrent"
        mode="horizontal"
        :items="menuItems"
      />
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'icon'"></template>
          <template v-if="column.dataIndex === 'authenticationStatus'">
            <template v-if="record[column.dataIndex] === 'DONE'">
              <a-tag color="green">已认证</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'DOING'">
              <a-tag color="gray">待审核</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'NOT_DONE'">
              <a-tag color="orange">未认证</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'REJECT'">
              <a-tag color="red">审核驳回</a-tag>
            </template>
            <template v-else>{{ record[column.dataIndex] }}</template>
          </template>

          <template v-if="column.dataIndex === 'code'">
            <template v-if="record['type'] === 'COMPANY'">
              {{ record["code"] }}
            </template>
            <template v-else-if="record['type'] === 'PERSON'">
              {{ record["idCard"] }}
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'type'">
            <template v-if="record[column.dataIndex] === 'COMPANY'">
              <a-tag color="blue">公司</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'PERSON'">
              <a-tag color="green">个人</a-tag>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <template v-if="record['authenticationStatus'] === 'DOING'">
              <a type="link" @click="handleAudit(record)">审核</a>
            </template>
            <template v-else>
              <a type="link" @click="handlePreview(record)">查看</a>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
    <company-audit-form-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
